import {ToolExecuteParams, ToolResult} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import toolError from 'common/src/modules/shared/copilot/utils/toolError';
import {CLStore} from 'common/src/redux/store';
import {z} from 'zod';
import assertAiGeneratedContentFeed, {
  CONTENT_FEED_AI_TOOL_CONTEXT_ERROR,
} from '../utils/assertAiGeneratedContentFeed';
import {ContentFeedAiGeneratedSkillToolName} from '../skills/contentFeedAiGeneratedSkill.enums';
import {
  ContentDraftItem,
  ContentFeed,
  ColumnPurpose,
  ColumnDataType,
} from 'common/src/modules/contentFeeds/contentFeeds.types';
import {Folder} from 'common/src/modules/Assets/Assets.types';
import findAssetById from 'common/src/modules/Assets/utils/findAssetById';
import {Persona} from 'common/src/modules/brandGuidelines/brandGuidelines.types';

const ISSUE_LEVEL_ERROR = 0;

const inputSchema = z.object({
  itemIds: z
    .array(z.number())
    .optional()
    .nullable()
    .describe('Specific draft item IDs to fetch. If omitted, all items are returned.'),
  includePromptAndReasoning: z
    .boolean()
    .optional()
    .nullable()
    .describe(
      'Include the generated prompt and reasoning for each creative column. Use only when working on prompts, and avoid on large sets of items.',
    ),
});

function csvEscape(value: string): string {
  if (value.includes(',') || value.includes('"')) {
    return `"${value.replace(/"/g, '""')}"`;
  }

  return value;
}

function getCreativeColumnStatus(
  item: ContentDraftItem,
  columnKey: string,
): string {
  const colData = item.columnValues?.[columnKey];
  const byAdSize = colData?.byAdSize;

  if (!byAdSize) return '';

  const hasError = Object.values(byAdSize).some((v) =>
    v?.generationIssues?.some((issue) => issue.level === ISSUE_LEVEL_ERROR),
  );

  if (hasError) return 'Error';

  const hasGenerated = Object.values(byAdSize).some((v) => !!v?.generationTimestamp);

  if (hasGenerated) return 'Generated';

  return '';
}

function formatDraftItemsCsv(
  items: ContentDraftItem[],
  feed: ContentFeed,
  assetTree: Folder | null,
  personaMap: Map<number, string>,
  includePromptAndReasoning?: boolean,
): string {
  const columns = feed.feedColumnsSchema?.columns ?? [];

  const inputCols = columns.filter((c) => c.purpose === ColumnPurpose.Input);
  const adDecisioningCols = columns.filter((c) => c.purpose === ColumnPurpose.AdDecisioning);
  const reportingCols = columns.filter((c) => c.purpose === ColumnPurpose.Reporting);
  const creativeCols = columns.filter((c) => c.purpose === ColumnPurpose.UsedInCreative);
  const imageColumnKeys = new Set(
    inputCols.filter((c) => c.type === ColumnDataType.Image).map((c) => c.columnKey),
  );
  const personaColumnKeys = new Set(
    inputCols.filter((c) => c.type === ColumnDataType.Persona).map((c) => c.columnKey),
  );

  const headerParts = ['id', 'name'];

  for (const col of inputCols) {
    headerParts.push(col.columnKey);
  }

  for (const col of adDecisioningCols) {
    headerParts.push(col.columnKey);
  }

  for (const col of reportingCols) {
    headerParts.push(col.columnKey);
  }

  for (const col of creativeCols) {
    headerParts.push(`${col.columnKey} (status)`);

    if (includePromptAndReasoning) {
      headerParts.push(`${col.columnKey} (prompt)`);
      headerParts.push(`${col.columnKey} (reasoning)`);
    }
  }

  const lines: string[] = [headerParts.join(',')];

  for (const item of items) {
    const parts: string[] = [
      String(item.id),
      csvEscape(item.name || ''),
    ];

    for (const col of inputCols) {
      const rawValue = String(item.columnValues?.[col.columnKey]?.value ?? '');

      if (imageColumnKeys.has(col.columnKey) && assetTree && rawValue) {
        const asset = findAssetById(assetTree, Number(rawValue));
        const title = asset?.contentMetadata?.aiContentMetadata?.metadataV1?.title;
        parts.push(csvEscape(title ? `${rawValue} (${title})` : rawValue));
      } else if (personaColumnKeys.has(col.columnKey) && rawValue) {
        const personaName = personaMap.get(Number(rawValue));
        parts.push(csvEscape(personaName ? `${rawValue} (${personaName})` : rawValue));
      } else {
        parts.push(csvEscape(rawValue));
      }
    }

    for (const col of adDecisioningCols) {
      parts.push(csvEscape(String(item.columnValues?.[col.columnKey]?.value ?? '')));
    }

    for (const col of reportingCols) {
      parts.push(csvEscape(String(item.columnValues?.[col.columnKey]?.value ?? '')));
    }

    for (const col of creativeCols) {
      parts.push(getCreativeColumnStatus(item, col.columnKey));

      if (includePromptAndReasoning) {
        const colData = item.columnValues?.[col.columnKey];
        parts.push(csvEscape(colData?.prompt ?? ''));
        parts.push(csvEscape(colData?.reasoning ?? ''));
      }
    }

    lines.push(parts.join(','));
  }

  return lines.join('\n');
}

export default function getDraftItemsTool(store: CLStore) {
  async function execute({
    input: {itemIds, includePromptAndReasoning},
  }: ToolExecuteParams<typeof inputSchema>): Promise<ToolResult> {
    const state = store.getState();
    const feed = state.contentFeedPage?.feed;

    if (!assertAiGeneratedContentFeed(feed)) {
      return toolError(CONTENT_FEED_AI_TOOL_CONTEXT_ERROR);
    }

    const allItems: ContentDraftItem[] = state.contentFeedPage?.draftItems?.items ?? [];

    if (allItems.length === 0) {
      return toolResult('No draft items exist for this content feed.');
    }

    let items = allItems;

    if (itemIds && itemIds.length > 0) {
      const idSet = new Set(itemIds);
      items = allItems.filter((item) => idSet.has(item.id));

      if (items.length === 0) {
        return toolError(
          `None of the requested item IDs (${itemIds.join(', ')}) were found. Available IDs: ${allItems.map((i) => i.id).join(', ')}`,
        );
      }
    }

    const assetTree = state.contentFeedPage?.contentFeedAssets?.assetTree ?? null;
    const allPersonas: Persona[] = state.brandGuidelinesPage?.brandPersonas?.data ?? [];
    const briefPersonaIds = new Set(feed.brief?.personaIds ?? []);
    const personaMap = new Map<number, string>(
      allPersonas
        .filter((p): p is Persona & {name: string} => briefPersonaIds.has(p.id) && !!p.name)
        .map((p) => [p.id, p.name]),
    );
    const csv = formatDraftItemsCsv(items, feed, assetTree, personaMap, includePromptAndReasoning ?? false);

    return toolResult(`Draft items (${items.length} of ${allItems.length} total):\n${csv}`);
  }

  return tool({
    name: ContentFeedAiGeneratedSkillToolName.GetDraftItems,
    displayName: 'Get Draft Items',
    description:
      'Fetch draft items with their input column values, ad decisioning values, reporting values, and creative column generation status. Optionally filter by item IDs.',
    inputSchema,
    execute,
    isReadonly: true,
  });
}
